import React, { useMemo } from 'react'

import { _div, _heading, useEditor } from '@toasttab/sites-components'

import { Block as BlockType } from 'src/apollo/sites'
import { EditableButton } from 'src/shared/components/common/button'
import EditableFeaturedMenuItemsGroup from 'src/shared/components/common/dynamic_menus/menu_group/FeaturedMenuItemsGroup'
import { EditableVideo } from 'src/shared/components/common/editable_video/Video'

import EditableMenuGroup from 'shared/components/common/dynamic_menus/menu_group/MenuGroup'
import EditableMenuItem from 'shared/components/common/dynamic_menus/menu_item/MenuItem'
import { _Image } from 'shared/components/common/editable_image/EditableImage'
import EmbeddedCode from 'shared/components/common/embedded_code/EmbeddedCode'
import { EditableShape } from 'shared/components/common/shape/Shape'
import EditableMap from 'shared/components/common/editable_map/EditableMap'

import {
  useBlockDimensionUtils,
  useDynamicSectionConfig,
  useDynamicSectionLayout
} from 'public/components/default_template/dynamic_section/DynamicSectionContext'
import { GenericMenuComponent } from 'src/shared/components/common/dynamic_menus/generic_menu_component/GenericMenuComponent'
import { CarouselV2 } from 'src/shared/components/common/editable_carousel/CarouselV2'

type BlockProps = {
  block: BlockType
  editPath: string
  isPopup?: boolean
}

type BlockWrapperProps = {
  block: BlockType
}

export const BlockWrapper = ({
  children,
  block
}: React.PropsWithChildren<BlockWrapperProps>) => {
  const { dimensionsFor } = useBlockDimensionUtils()
  const { offsetByGuid } = useDynamicSectionLayout()

  const gridArea = useMemo(() => {
    const { startX, endX, startY, endY } = dimensionsFor(
      block,
      offsetByGuid[block.guid] ?? 0
    )
    return `${startY}/${startX}/${endY}/${endX}`
  }, [block, dimensionsFor, offsetByGuid])

  return (
    <div
      data-testid='blockWrapper'
      className='blockWrapper'
      style={{ gridArea, zIndex: block.layer, display: 'flex', height: '100%' }}
    >
      {children}
    </div>
  )
}

export const Block = ({ block, editPath, isPopup }: BlockProps) => {
  const { screenType } = useDynamicSectionConfig()
  const { rowsAndColsFor } = useBlockDimensionUtils()
  const { numRows, numCols, mobileNumCols, mobileNumRows } =
    rowsAndColsFor(block)
  const aspectRatio =
    screenType === 'mobile'
      ? `${mobileNumCols} / ${mobileNumRows}`
      : `${numCols} / ${numRows}`
  const { isEditor } = useEditor()

  // Circles must preserve aspect ratio on mobile and desktop, otherwise they look like an oval
  const isCircle = block.contents?.type === 'Circle'

  const aspectRatioWithFullDimensions = {
    width: '100%',
    aspectRatio,
    minWidth: 0,
    minHeight: 0
  }
  switch (block.kind) {
    case 'paragraph':
    case 'text':
      return (
        <_div
          editPath={editPath}
          html={
            typeof block.contents === 'string'
              ? block.contents
              : JSON.stringify(block.contents)
          }
          hideOutline
        />
      )
    case 'heading1':
      return (
        <_heading
          editPath={editPath}
          styleLevel={1}
          html={
            typeof block.contents === 'string'
              ? block.contents
              : JSON.stringify(block.contents)
          }
          hideOutline
        />
      )
    case 'heading2':
      return (
        <_heading
          editPath={editPath}
          styleLevel={2}
          html={
            typeof block.contents === 'string'
              ? block.contents
              : JSON.stringify(block.contents)
          }
          hideOutline
        />
      )
    case 'heading3':
      return (
        <_heading
          editPath={editPath}
          styleLevel={3}
          html={
            typeof block.contents === 'string'
              ? block.contents
              : JSON.stringify(block.contents)
          }
          hideOutline
        />
      )
    case 'heading4':
      return (
        <_heading
          editPath={editPath}
          styleLevel={4}
          html={
            typeof block.contents === 'string'
              ? block.contents
              : JSON.stringify(block.contents)
          }
          hideOutline
        />
      )
    case 'image':
      return (
        <_Image
          style={{
            height: '100%',
            width: '100%',
            aspectRatio,
            minWidth: 0,
            minHeight: 0
          }}
          borderAndShadowConfig={block.borderAndShadowConfig}
          overrideGlobalBorderAndShadowConfig={
            block.overrideGlobalBorderAndShadowConfig
          }
          imageObjectPath={editPath}
          imageObject={block.contents}
          alt={block.contents?.altText ?? ''}
          decorated={true}
        />
      )
    case 'video':
      return (
        <EditableVideo
          borderAndShadowConfig={block.borderAndShadowConfig}
          overrideGlobalBorderAndShadowConfig={
            block.overrideGlobalBorderAndShadowConfig
          }
          editPath={editPath}
          {...block.contents}
          styles={!isEditor ? aspectRatioWithFullDimensions : {}}
        />
      )
    case 'button':
      return (
        <div className='centerAligned'>
          <EditableButton editPath={editPath} {...block.contents} />
        </div>
      )
    case 'shape':
      return (
        <EditableShape
          editPath={editPath}
          {...block.contents}
          styles={
            !isEditor && (screenType === 'mobile' || isCircle)
              ? aspectRatioWithFullDimensions
              : {}
          }
        />
      )
    case 'menuItem':
      return (
        <EditableMenuItem
          editPath={editPath}
          {...block.contents}
          borderAndShadowConfig={block.borderAndShadowConfig}
          overrideGlobalBorderAndShadowConfig={
            block.overrideGlobalBorderAndShadowConfig
          }
          styles={!isEditor ? aspectRatioWithFullDimensions : {}}
        />
      )
    case 'carousel': {
      const carouselOptions = {
        showArrows: block.contents?.showArrows,
        showSequenceDots: block.contents?.showSequenceDots,
        loop: block.contents?.loop,
        autoPlayOn: block.contents?.autoPlayOn,
        autoPlayTime: block.contents?.autoPlayTime,
        dotShape: block.contents?.dotShape,
        dotOutline: block.contents?.dotOutline,
        dotNumbers: block.contents?.dotNumbers
      }
      return (
        <CarouselV2
          editPath={editPath}
          images={block.contents?.images}
          options={carouselOptions}
          style={aspectRatioWithFullDimensions}
        />
      )
    }
    case 'menuGroup':
      return (
        <EditableMenuGroup
          editPath={editPath}
          {...block.contents}
          borderAndShadowConfig={block.borderAndShadowConfig}
          overrideGlobalBorderAndShadowConfig={
            block.overrideGlobalBorderAndShadowConfig
          }
          styles={!isEditor ? aspectRatioWithFullDimensions : {}}
        />
      )

    case 'featuredMenuItems':
      return (
        <EditableFeaturedMenuItemsGroup
          editPath={editPath}
          {...block.contents}
          borderAndShadowConfig={block.borderAndShadowConfig}
          overrideGlobalBorderAndShadowConfig={
            block.overrideGlobalBorderAndShadowConfig
          }
          styles={!isEditor ? aspectRatioWithFullDimensions : {}}
        />
      )
    case 'genericMenuComponent':
      return (
        <GenericMenuComponent
          editPath={editPath}
          styles={!isEditor ? aspectRatioWithFullDimensions : {}}
        />
      )
    case 'embeddedCode':
      return (
        <EmbeddedCode
          editPath={editPath}
          block={block}
          isPopup={isPopup}
          styles={!isEditor ? aspectRatioWithFullDimensions : {}}
        />
      )
    case 'map':
      return (
        <EditableMap
          editPath={editPath}
          block={block}
          styles={!isEditor ? aspectRatioWithFullDimensions : {}}
        />
      )
    default:
      return null
  }
}
